import {
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
} from '@blocksuite/affine-block-database';
import {
  AutomationConnectorTool,
  DEFAULT_AUTOMATION_HEIGHT,
  DEFAULT_AUTOMATION_WIDTH,
} from '@blocksuite/affine-block-streamlain';
import { DefaultTool } from '@blocksuite/affine-block-surface';
import { menu } from '@blocksuite/affine-components/context-menu';
import {
  type EdgelessToolbarI18nKey,
  edgelessToolbarI18nT,
  type ElementCreationSource,
} from '@blocksuite/affine-shared/services';
import {
  type DenseMenuBuilder,
  EdgelessToolbarToolMixin,
  QuickToolExtension,
  QuickToolMixin,
} from '@blocksuite/affine-widget-edgeless-toolbar';
import { Bound } from '@blocksuite/global/gfx';
import { DualLinkIcon } from '@blocksuite/icons/lit';
import type { BlockComponent } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { css, html, LitElement, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import {
  automationDenseIcon,
  automationIcon,
  automationRender,
  databaseDenseIcon,
  databaseRender,
  type StreamLainDraggableTool,
} from './streamlain-basket-elements.js';

const WORKFLOW_ENABLED_CLASS = 'workflow-enabled';

export type StreamLainQuickToolKind = 'database' | 'automation';

export type StreamLainFlowMenuAction =
  | StreamLainQuickToolKind
  | 'automationConnector';

type StreamLainQuickToolItem = {
  type: StreamLainQuickToolKind;
  denseIcon: TemplateResult;
  render: StreamLainDraggableTool['render'];
  standardWidth: number;
  standardHeight: number;
  tooltipKey: EdgelessToolbarI18nKey;
};

const databaseItem: StreamLainQuickToolItem = {
  type: 'database',
  denseIcon: databaseDenseIcon,
  render: databaseRender,
  standardWidth: DEFAULT_CANVAS_DATABASE_WIDTH,
  standardHeight: DEFAULT_CANVAS_DATABASE_HEIGHT,
  tooltipKey: 'toolbar.database',
};

const automationItem: StreamLainQuickToolItem = {
  type: 'automation',
  denseIcon: automationDenseIcon,
  render: automationRender,
  standardWidth: DEFAULT_AUTOMATION_WIDTH,
  standardHeight: DEFAULT_AUTOMATION_HEIGHT,
  tooltipKey: 'toolbar.automation',
};

export function createStreamLainQuickToolAtCenter(
  kind: StreamLainQuickToolKind,
  edgeless: BlockComponent | undefined,
  control: ElementCreationSource = 'toolbar:general'
) {
  if (!edgeless) {
    return Promise.resolve(null);
  }
  if (kind === 'automation' && !isStreamLainWorkflowEnabled()) {
    return Promise.resolve(null);
  }

  const item = kind === 'automation' ? automationItem : databaseItem;
  const { x, y } = edgeless.std.get(GfxControllerIdentifier).viewport.center;
  const bound = new Bound(
    x - item.standardWidth / 2,
    y - item.standardHeight / 2,
    item.standardWidth,
    item.standardHeight
  );

  return item.render(bound, edgeless, control);
}

export function isStreamLainWorkflowEnabled() {
  return (
    typeof document !== 'undefined' &&
    document.body.classList.contains(WORKFLOW_ENABLED_CLASS)
  );
}

function buildStreamLainCreateAction(
  item: StreamLainQuickToolItem,
  edgeless: BlockComponent
) {
  return menu.action({
    name: edgelessToolbarI18nT(item.tooltipKey),
    prefix: item.denseIcon,
    class: { 'data-view-menu-entry': true },
    select: () => {
      createStreamLainQuickToolAtCenter(
        item.type,
        edgeless,
        'toolbar:general'
      ).catch(console.error);
    },
  });
}

const buildStreamLainFlowDenseMenu: DenseMenuBuilder = (edgeless, gfx) => {
  const items = [
    buildStreamLainCreateAction(databaseItem, edgeless),
    ...(isStreamLainWorkflowEnabled()
      ? [
          buildStreamLainCreateAction(automationItem, edgeless),
          menu.action({
            name: edgelessToolbarI18nT('toolbar.automationConnector'),
            prefix: DualLinkIcon({ width: '20px', height: '20px' }),
            select: () => gfx.tool.setTool(AutomationConnectorTool),
            isSelected:
              gfx.tool.currentToolName$.peek() ===
              AutomationConnectorTool.toolName,
          }),
        ]
      : []),
  ];

  return menu.subMenu({
    name: edgelessToolbarI18nT('toolbar.automation'),
    prefix: automationDenseIcon,
    isSelected:
      gfx.tool.currentToolName$.peek() === AutomationConnectorTool.toolName,
    options: { items },
  });
};

export class EdgelessStreamLainFlowMenu extends EdgelessToolbarToolMixin(
  LitElement
) {
  static override styles = css`
    :host {
      position: absolute;
      display: flex;
      z-index: -1;
    }

    .streamlain-flow-menu-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 14px;
    }

    .streamlain-flow-menu-content svg {
      display: block;
      width: 20px;
      height: 20px;
    }
  `;

  override type = AutomationConnectorTool;

  override render() {
    const workflowEnabled = isStreamLainWorkflowEnabled();

    return html`<edgeless-slide-menu>
      <div class="streamlain-flow-menu-content">
        <edgeless-tool-icon-button
          data-testid="streamlain-flow-menu-database"
          .activeMode=${'background'}
          .tooltip=${edgelessToolbarI18nT(databaseItem.tooltipKey)}
          @click=${() => this.onSelect('database')}
        >
          ${databaseDenseIcon}
        </edgeless-tool-icon-button>

        ${workflowEnabled
          ? html`
              <edgeless-tool-icon-button
                data-testid="streamlain-flow-menu-automation"
                .activeMode=${'background'}
                .tooltip=${edgelessToolbarI18nT(automationItem.tooltipKey)}
                @click=${() => this.onSelect('automation')}
              >
                ${automationDenseIcon}
              </edgeless-tool-icon-button>

              <edgeless-tool-icon-button
                data-testid="streamlain-flow-menu-connector"
                .active=${this.active}
                .activeMode=${'background'}
                .tooltip=${edgelessToolbarI18nT('toolbar.automationConnector')}
                @click=${() => this.onSelect('automationConnector')}
              >
                ${DualLinkIcon()}
              </edgeless-tool-icon-button>
            `
          : null}
      </div>
    </edgeless-slide-menu>`;
  }

  @property({ attribute: false })
  accessor onSelect!: (action: StreamLainFlowMenuAction) => void;
}

export class EdgelessStreamLainFlowToolButton extends QuickToolMixin(
  LitElement
) {
  static override styles = css`
    :host {
      display: flex;
    }
  `;

  override type = AutomationConnectorTool;

  private _disposeMenu() {
    if (!this.popper) return false;
    this.popper.dispose();
    this.popper = null;
    return true;
  }

  private _toggleMenu() {
    if (this._disposeMenu()) return;

    const menu = this.createPopper('edgeless-streamlain-flow-menu', this);
    menu.element.edgeless = this.edgeless;
    menu.element.onSelect = action => {
      if (action === 'automationConnector') {
        this.setEdgelessTool(AutomationConnectorTool);
        this._disposeMenu();
        return;
      }

      createStreamLainQuickToolAtCenter(action, this.edgeless)
        .then(id => {
          if (id) this.setEdgelessTool(DefaultTool);
        })
        .catch(console.error)
        .finally(() => this._disposeMenu());
    };
  }

  override render() {
    return html`<edgeless-tool-icon-button
      data-testid="edgeless-streamlain-flow-tool-button"
      .tooltip=${this.popper ? '' : edgelessToolbarI18nT('toolbar.automation')}
      .tooltipOffset=${17}
      .active=${this.active}
      .iconContainerPadding=${6}
      .iconSize=${'24px'}
      @click=${this._toggleMenu}
    >
      ${automationIcon}
      <toolbar-arrow-up-icon></toolbar-arrow-up-icon>
    </edgeless-tool-icon-button>`;
  }
}

export const streamLainFlowQuickTool = QuickToolExtension(
  'streamlainFlow',
  ({ block, gfx }) => {
    return {
      type: 'streamlainFlow',
      content: html`<edgeless-streamlain-flow-tool-button
        .edgeless=${block}
      ></edgeless-streamlain-flow-tool-button>`,
      menu: buildStreamLainFlowDenseMenu(block, gfx),
      priority: 79.5,
    };
  }
);
